// Shared formatting and domain vocabulary for the console.
// The backend sends naive UTC timestamps; everything is shown in Gulf Standard Time.

export const TZ = "Asia/Dubai";

export function toDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "number") return new Date(ts);
  const s = String(ts);
  const iso = /Z|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtTime(ts, withSeconds = true) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleTimeString("en-GB", {
    timeZone: TZ,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
  });
}

export function fmtDateTime(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    timeZone: TZ,
    hour12: false,
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function fmtDate(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { timeZone: TZ, day: "2-digit", month: "short", year: "numeric" });
}

export function timeAgo(ts, now = Date.now()) {
  const d = toDate(ts);
  if (!d) return "—";
  const s = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function duration(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${Math.max(0, Math.round(secs))}s`;
  if (secs < 3600) return `${Math.ceil(secs / 60)} min`;
  return `${(secs / 3600).toFixed(1)} h`;
}

export const num = (v) => (v == null || Number.isNaN(v) ? "—" : Number(v).toLocaleString("en-US"));

export function compact(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  return n.toLocaleString("en-US");
}

export const pct = (v, d = 1) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(d)}%`);

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ── Severity ──────────────────────────────────────────────────────
export const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];
export const SEV_RANK = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1, INFO: 0 };
export const sevVar = (s) => `var(--sev-${String(s || "INFO").toLowerCase()})`;
export const sevTone = (s) => String(s || "INFO").toLowerCase();
export const sevLabel = (s) => {
  const v = String(s || "INFO");
  return v.charAt(0) + v.slice(1).toLowerCase();
};
export function worstSeverity(list) {
  let best = null;
  for (const s of list) if (best == null || (SEV_RANK[s] ?? -1) > (SEV_RANK[best] ?? -1)) best = s;
  return best;
}

// ── Attack vocabulary ─────────────────────────────────────────────
export const ATTACK_LABELS = {
  ddos: "DDoS flood",
  port_scan: "Port scan",
  brute_force: "Brute force",
  brute_force_attempt: "Failed login",
  sql_injection: "SQL injection",
  xss: "Cross-site scripting",
  web_attack: "Web attack",
  malware_c2: "Malware C2",
  data_exfiltration: "Data exfiltration",
  ransomware: "Ransomware",
  privilege_escalation: "Privilege escalation",
  lateral_movement: "Lateral movement",
  anomalous_traffic: "Anomalous traffic",
  honeypot: "Honeypot trap",
  botnet_ddos: "Botnet DDoS",
  normal: "Normal",
};
export const attackLabel = (t) =>
  ATTACK_LABELS[t] || String(t || "unknown").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Categorical palette for attack types (tokens defined in index.css)
const ATTACK_HUES = {
  ddos: 1,
  port_scan: 2,
  brute_force: 3,
  brute_force_attempt: 3,
  sql_injection: 4,
  xss: 5,
  web_attack: 5,
  malware_c2: 6,
  data_exfiltration: 7,
  ransomware: 8,
  privilege_escalation: 4,
  lateral_movement: 2,
  anomalous_traffic: 0,
};
export const attackVar = (t) => `var(--cat-${ATTACK_HUES[t] ?? 0})`;

export const isIPv4 = (s) =>
  /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(String(s || "").trim());
